import React, { useRef, useState } from "react";
import { useDrag, useDrop } from 'react-dnd';
import { MenuDotsSvg, EditPencilSvg, TrashBinSvg } from "components/icons";
import { Tooltip, SecondaryTextXs300, SecondaryTextBase, SecondaryTextSm400, Button, ClickOutsideWrapper } from "components/atoms";
import styled, { css } from "styled-components";
import { colorShade, prioritySettings, getPriorityIconByName } from 'utils';

const Row = styled.div`
  ${({ isDragging, theme }) => css`
    border-bottom: 1px solid #e3e3e3;
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    height: 57px;
    opacity: ${isDragging ? .5 : 1};

    background-color: ${isDragging ? `${theme?.colour?.primary ?? '#1DBD6B'}26` : ''};
  `}
`;

const Element = styled.div`
  ${({ color, background }) => css`
    color: ${color};
    font-size: 14px;
    background: ${background};
    display: flex;
    align-items: center;
    padding: 6px 12px;
    gap: 2px;
    border-radius: 40px;
    text-transform: uppercase;
  `}
`

const DeleteWrapper = styled.div`
  background: #FFFFFF;
  border: 1px solid #E3E3E3;
  border-radius: 8px;
  padding: 16px;
  position: absolute;
  margin-top: 10px;
  left: 0px;
  z-index: 10;
`
const priorityTooltip = (assignedOrders, affectsOrders, assignedTags) => {
  if (assignedOrders && affectsOrders) {
    return `This priority is assigned to work orders ${assignedTags ? ', inspection tags ' : ''}and affects settings. It cannot be deleted`
  } else {
    if (affectsOrders) {
      return `This priority ${assignedTags ? 'is assigned to inspection tags and ' : ''}affects work order settings. It cannot be deleted`
    }
    if (assignedOrders) {
      return `This priority is assigned to work orders ${assignedTags ? 'and inspection tags ' : ''}. It cannot be deleted`
    }
    if (assignedTags) {
      return "This priority is assigned to inspection tags and cannot be deleted"
    }
    return '';
  }
}

export const SwitchField = ({ onDelete, move, onEdit, priority, hideDelete, index, save }) => {
  if (!priority) return null;

  const ref = useRef(null);

  const [isDelete, setIsDelete] = useState(false);

  const [{ handlerId }, drop] = useDrop({
    accept: 'card',
    collect(monitor) {
      return {
        handlerId: monitor.getHandlerId(),
      };
    },
    hover(item, monitor) {
      if (!ref.current) {
        return;
      }
      const dragIndex = item.index;
      const hoverIndex = index;
      if (dragIndex === hoverIndex) {
        return;
      }
      const hoverBoundingRect = ref.current?.getBoundingClientRect();
      const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;
      const clientOffset = monitor.getClientOffset();
      const hoverClientY = clientOffset.y - hoverBoundingRect.top;
      if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
        return;
      }
      if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
        return;
      }
      move(dragIndex, hoverIndex);
      item.index = hoverIndex;
    },
    drop() {
      save();
    }
  });

  const [{ isDragging }, drag] = useDrag({
    type: 'card',
    item: () => {
      return { name, index };
    },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });
  drag(drop(ref));

  const onToggle = () => {
    setIsDelete(!isDelete);
  }
  const priorityToolTipList = priorityTooltip(priority.assigned_orders, priority.affects_orders, priority.assigned_tags).split('and');
  return (
    <Row ref={ref} data-handler-id={handlerId} isDragging={isDragging}>
      <div className="flex items-center">
        <div className="cursor-move mr-3">
          <MenuDotsSvg multiple />
        </div>
        <Element color={prioritySettings[priority?.color]?.textColor} background={prioritySettings[priority?.color]?.background}>
          <div>{getPriorityIconByName({ name: priority?.icon, color: prioritySettings[priority?.color]?.textColor, width: 15, height: 15 })}</div>
          <div>{priority.name}</div>
        </Element>
        {priority?.is_default &&
          <div className="ml-2">
            <SecondaryTextXs300>Default</SecondaryTextXs300>
          </div>
        }
      </div>

      <td>
        <div className="flex items-center">
          {!hideDelete && !priority?.is_default &&
            <div className={`mr-3 ${!priority?.assigned_orders && !priority.affects_orders && !priority.assigned_tags && 'cursor-pointer'}`} onClick={() => !priority.assigned_orders && !priority.affects_orders && !priority.assigned_tags && onToggle()}>
              {(priority?.assigned_orders || priority?.affects_orders || priority?.assigned_tags) ?
                <Tooltip direction="right" delay={100} content={
                  <div className="whitespace-nowrap">
                    <div>{priorityToolTipList[0]}</div>
                    {priorityToolTipList.length > 1 && (
                      <div>and {priorityToolTipList[1]}</div>
                    )}
                  </div>
                }>
                  <TrashBinSvg width={15} height={15} color={!priority.assigned_orders && !priority.affects_orders && !priority?.assigned_tags ? window?.settings?.primary_color ?? '#1DBD6B' : '#E3E3E3'} />
                </Tooltip> :
                <ClickOutsideWrapper onDismiss={() => setIsDelete(false)}>
                  <div className="relative">
                    <Tooltip direction="bottom" delay={100} content={
                      <div className="whitespace-nowrap">Delete</div>
                    }>
                      <TrashBinSvg width={15} height={15} color={!priority.assigned_orders && !priority.affects_orders && !priority?.assigned_tags ? window?.settings?.primary_color ?? '#1DBD6B' : '#E3E3E3'} />
                    </Tooltip>
                    {isDelete &&
                      <DeleteWrapper >
                        <SecondaryTextBase>{`Delete ${priority?.name ?? ''}`}</SecondaryTextBase>
                        <div className="mt-2">
                          <SecondaryTextSm400>This cannot be undone</SecondaryTextSm400>
                        </div>
                        <div className="mt-4 flex flex-center">
                          <div className="mr-4">
                            <Button style='outlined' state='action' title='Cancel' onClick={() => onToggle()} />
                          </div>
                          <Button style='solid' state='delete' title='Delete' onClick={() => { onDelete(priority); onToggle() }} />
                        </div>
                      </DeleteWrapper>
                    }
                  </div>
                </ClickOutsideWrapper>
              }
            </div>
          }
          <div className="ml-3 cursor-pointer" onClick={() => onEdit(priority)}>
            <Tooltip direction="bottom" delay={100} content={
              <div className="whitespace-nowrap">Edit</div>
            }>
              <EditPencilSvg width={15} height={15} color={window?.settings?.primary_color ?? '#1DBD6B'} />
            </Tooltip>
          </div>
        </div>
      </td>
    </Row>
  )
}